// src/utils/stableSelect.js
//
// Prerender determinism (Project B): featured/related carousels select a subset
// (feature card, slice(0,N), seeded shuffle) from a fetched collection. If the
// collection's order is not TOTAL, the same records can arrive in a different order
// and the selection drifts between renders. The Supabase queries now append a unique
// `id` tiebreaker, and these pure helpers give the SAME guarantee client-side —
// independent of database delivery order — so it is unit-testable without a DB and
// robust even if a query's order is ever weakened.
//
// This does NOT change eligibility, filters, or how many records are returned; it only
// makes the input order deterministic before selection. Live rotation still happens
// (real Math.random over a stable input); the prerender's seeded RNG makes it repeatable.

// Unique final tiebreaker on a stable, always-present key (id). String-compared so it
// works for uuid or numeric ids without NaN surprises.
export function tieById(a, b) {
  return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
}

// A total, stable order: apply the given primary comparators in sequence, then ALWAYS
// fall back to the id tiebreaker. Returns a new array (does not mutate input).
export function orderBy(rows, ...comparators) {
  return [...(rows || [])].sort((a, b) => {
    for (const cmp of comparators) { const r = cmp(a, b); if (r) return r; }
    return tieById(a, b);
  });
}

// Comparator factory: descending by a date-ish field (published_at/updated_at/created_at),
// null/absent dates sort last. Ties defer to the next comparator (ultimately tieById).
export function byDateDesc(field, ...fallbacks) {
  const t = (v) => { const n = v == null ? NaN : new Date(v).getTime(); return Number.isNaN(n) ? -Infinity : n; };
  return (a, b) => {
    const d = t(b?.[field]) - t(a?.[field]);
    if (d) return d;
    for (const f of fallbacks) { const r = t(b?.[f]) - t(a?.[f]); if (r) return r; }
    return 0;
  };
}

export default { tieById, orderBy, byDateDesc };
